import { Component } from "@theme/component";
import { onDocumentReady } from "@theme/utilities";

/**
 * A custom element that manages the header-OD section, including mega menu hover interactions.
 *
 * @extends {Component}
 */
class HeaderOD extends Component {
  /**
   * Abort controller for cleaning up event listeners
   * @type {AbortController}
   */
  #abortController = new AbortController();

  /**
   * Mutation observers for watching details elements
   * @type {Set<MutationObserver>}
   */
  #detailsObservers = new Set();

  /**
   * DOM observer for watching new mega menus
   * @type {MutationObserver | null}
   */
  #domObserver = null;

  /**
   * Resize observer for updating header height
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.documentElement.style.setProperty(
      "--header-height",
      `${height}px`
    );

    // Recalculate announcement bar heights when header resizes
    this.#updateAnnouncementBarOffsets();
  });

  /**
   * Update announcement bar offsets for sticky positioning
   */
  #updateAnnouncementBarOffsets() {
    const section = this.closest(`[id^='shopify-section-']`);
    if (!(section instanceof HTMLElement)) return;

    let totalAnnouncementHeight = 0;
    let announcementBarCount = 0;
    
    // Find all announcement bars before this header
    const allSections = document.querySelectorAll('[id^="shopify-section-"]');
    for (const sectionElement of allSections) {
      if (sectionElement === section) break;
      
      if (sectionElement instanceof HTMLElement) {
        if (sectionElement.classList.contains('shopify-section--announcement-bar') ||
            sectionElement.querySelector('.announcement-bar')) {
          const computedStyle = window.getComputedStyle(sectionElement);
          if (computedStyle.position === 'sticky') {
            const barHeight = sectionElement.getBoundingClientRect().height;
            totalAnnouncementHeight += barHeight;
            announcementBarCount++;
            
            // Set individual announcement bar height variables
            if (announcementBarCount === 1) {
              document.documentElement.style.setProperty(
                "--announcement-bar-height",
                `${Math.round(barHeight)}px`
              );
            } else if (announcementBarCount === 2) {
              document.documentElement.style.setProperty(
                "--announcement-bar-height-2",
                `${Math.round(barHeight)}px`
              );
            }
          }
        }
      }
    }
    
    // Update section top position to account for all sticky announcement bars
    if (totalAnnouncementHeight > 0) {
      section.style.setProperty(
        "--total-announcement-height",
        `${Math.round(totalAnnouncementHeight)}px`
      );
      section.style.top = `${totalAnnouncementHeight}px`;
    } else {
      section.style.top = "0px";
    }
  }

  /**
   * The last recorded scrollTop of the document
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation
   * @constant {number}
   */
  #animationDelay = 150;

  connectedCallback() {
    super.connectedCallback();

    // Setup mega menu hover listeners
    this.#setupMegaMenuListeners();

    // Setup header height observer
    this.#resizeObserver.observe(this);

    // Setup scroll direction detection
    this.#setupScrollDirection();

    // Update header height on initial load
    onDocumentReady(() => {
      this.#updateHeaderHeight();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#resizeObserver.disconnect();

    // Clean up mutation observers
    this.#detailsObservers.forEach((observer) => observer.disconnect());
    this.#detailsObservers.clear();

    if (this.#domObserver) {
      this.#domObserver.disconnect();
    }
  }

  /**
   * Load the vertical-nav.js script if not already loaded
   */

  /**
   * Update header height CSS variable and calculate announcement bar offsets
   */
  #updateHeaderHeight() {
    const height = this.getBoundingClientRect().height;
    document.documentElement.style.setProperty(
      "--header-height",
      `${Math.round(height)}px`
    );
    // Calculate announcement bar offsets
    this.#updateAnnouncementBarOffsets();
  }

  /**
   * Get or create backdrop element
   */
  #getBackdrop() {
    const sectionId = this.closest("[id^='shopify-section-']")?.id;
    if (!sectionId) return null;
    
    const backdropId = `mega-menu-backdrop-${sectionId}`;
    let backdrop = document.getElementById(backdropId);
    
    if (!backdrop) {
      backdrop = document.createElement("div");
      backdrop.className = "mega-menu-backdrop";
      backdrop.id = backdropId;
      document.body.appendChild(backdrop);
    } else if (backdrop.parentElement !== document.body) {
      // If backdrop exists but is not in body, move it to body for proper fixed positioning
      document.body.appendChild(backdrop);
    }
    
    return backdrop;
  }

  /**
   * Update backdrop visibility and position based on active mega menus
   */
  #updateBackdrop() {
    const backdrop = this.#getBackdrop();
    if (!backdrop) return;

    const activeMegaMenus = this.querySelectorAll(
      ".mega-menu[data-parent-link].active"
    );
    
    if (activeMegaMenus.length > 0) {
      // Calculate the bottom of the nav row to position backdrop below it
      const navRow = this.querySelector(".header__nav-row");
      if (navRow instanceof HTMLElement) {
        const navRowRect = navRow.getBoundingClientRect();
        const navRowBottom = navRowRect.bottom;
        backdrop.style.top = `${navRowBottom}px`;
      } else {
        // Fallback to header height if nav row not found
        const headerHeight = this.getBoundingClientRect().height;
        backdrop.style.top = `${headerHeight}px`;
      }
      backdrop.classList.add("active");
    } else {
      backdrop.classList.remove("active");
    }
  }

  /**
   * Setup mega menu hover listeners and sync with details elements
   */
  #setupMegaMenuListeners() {
    // Watch for details[open] changes and sync with mega menu active class
    /**
     * @param {HTMLElement} details
     */
    const setupDetailsObserver = (details) => {
      if (!(details instanceof HTMLElement)) return;
      
      const megaMenu = details.querySelector(".mega-menu[data-parent-link]");
      if (!megaMenu) {
        return;
      }

      // Check if already observed
      if (details.dataset.observed) return;
      details.dataset.observed = "true";

      // Sync on details open/close
      const observer = new MutationObserver(() => {
        if (details.hasAttribute("open")) {
          megaMenu.classList.add("active");
        } else {
          // Only remove active if not hovering
          if (!megaMenu.matches(":hover") && !details.matches(":hover")) {
            megaMenu.classList.remove("active");
          }
        }
        this.#updateBackdrop();
      });

      observer.observe(details, {
        attributes: true,
        attributeFilter: ["open"],
      });

      this.#detailsObservers.add(observer);

      // Use a single timeout to manage menu closing
      /** @type {ReturnType<typeof setTimeout> | null} */
      let closeTimeout = null;

      // Use a timeout to manage menu opening with delay
      /** @type {ReturnType<typeof setTimeout> | null} */
      let openTimeout = null;

      /**
       * Check if any mega menu is currently open
       * @returns {boolean}
       */
      const isAnyMegaMenuOpen = () => {
        const allDetails = this.querySelectorAll(
          'details[is="mega-menu-disclosure"]'
        );
        return Array.from(allDetails).some((other) => {
          if (!(other instanceof HTMLElement)) return false;
          if (other === details) return false;
          return other.hasAttribute("open") || 
                 other.querySelector(".mega-menu[data-parent-link]")?.classList.contains("active");
        });
      };

      const openMenu = () => {
        // Cancel any pending close
        if (closeTimeout) {
          clearTimeout(closeTimeout);
          closeTimeout = null;
        }

        // Cancel any pending open (in case of rapid hover changes)
        if (openTimeout) {
          clearTimeout(openTimeout);
          openTimeout = null;
        }

        // Check if any mega menu is already open - if so, skip delay
        const anyMenuOpen = isAnyMegaMenuOpen();
        const delay = anyMenuOpen ? 0 : 300;

        const performOpen = () => {
          // Close all other mega menus before opening this one
          const allDetails = this.querySelectorAll(
            'details[is="mega-menu-disclosure"]'
          );
          allDetails.forEach((other) => {
            if (!(other instanceof HTMLElement)) return;
            if (other === details) return;

            if (other.hasAttribute("open")) {
              other.removeAttribute("open");
            }

            const otherMegaMenu = other.querySelector(
              ".mega-menu[data-parent-link]"
            );
            if (otherMegaMenu instanceof HTMLElement) {
              otherMegaMenu.classList.remove("active");
            }
          });

          // Open the focused mega menu
          megaMenu.classList.add("active");
          details.setAttribute("open", "");
          this.#updateBackdrop();
          openTimeout = null;
        };

        if (delay === 0) {
          // Open immediately if a menu is already open
          performOpen();
        } else {
          // Add delay before opening (300ms) if no menu is open
          openTimeout = setTimeout(performOpen, delay);
        }
      };
      
      const scheduleClose = () => {
        // Cancel any pending open
        if (openTimeout) {
          clearTimeout(openTimeout);
          openTimeout = null;
        }

        if (closeTimeout) {
          clearTimeout(closeTimeout);
        }
        closeTimeout = setTimeout(() => {
          // Check if mouse is still over any part of the menu system
          const isHovering = 
            details.matches(":hover") ||
            megaMenu.matches(":hover") ||
            this.#isAnyMenuItemHovered(details);
          
          if (!isHovering) {
            megaMenu.classList.remove("active");
            details.removeAttribute("open");
            this.#updateBackdrop();
          }
          closeTimeout = null;
        }, 150);
      };

      // Get summary element once
      const summary = details.querySelector("summary");
      
      // Cancel open timeout if mouse leaves before delay completes
      const cancelOpen = () => {
        if (openTimeout) {
          clearTimeout(openTimeout);
          openTimeout = null;
        }
      };
      
      // Open menu when hovering over any part of the menu system
      megaMenu.addEventListener(
        "mouseenter",
        openMenu,
        { signal: this.#abortController.signal }
      );

      // Handle summary hover
      if (summary) {
        summary.addEventListener(
          "mouseenter",
          openMenu,
          { signal: this.#abortController.signal }
        );
        summary.addEventListener(
          "mouseleave",
          (e) => {
            const mouseEvent = /** @type {MouseEvent} */ (e);
            const relatedTarget = mouseEvent.relatedTarget;
            
            // Don't cancel if mouse is moving to details or mega menu
            if (relatedTarget instanceof Node) {
              if (details.contains(relatedTarget) || details === relatedTarget) {
                return;
              }
              if (megaMenu.contains(relatedTarget) || megaMenu === relatedTarget) {
                return;
              }
            }
            
            cancelOpen();
            scheduleClose();
          },
          { signal: this.#abortController.signal }
        );
      }
      
      // Open menu when entering details element
      details.addEventListener(
        "mouseenter",
        openMenu,
        { signal: this.#abortController.signal }
      );
      
      details.addEventListener(
        "mouseleave",
        (e) => {
          const mouseEvent = /** @type {MouseEvent} */ (e);
          const relatedTarget = mouseEvent.relatedTarget;
          
          // Don't cancel if mouse is moving to summary or mega menu
          if (relatedTarget instanceof Node) {
            if (summary && (summary.contains(relatedTarget) || summary === relatedTarget)) {
              return;
            }
            if (megaMenu.contains(relatedTarget) || megaMenu === relatedTarget) {
              return;
            }
          }
          
          cancelOpen();
          scheduleClose();
        },
        { signal: this.#abortController.signal }
      );
      
      megaMenu.addEventListener(
        "mouseleave",
        (e) => {
          const mouseEvent = /** @type {MouseEvent} */ (e);
          const relatedTarget = mouseEvent.relatedTarget;
          
          // Don't close if mouse is moving back to summary or details
          if (relatedTarget instanceof Node) {
            if (summary && (summary.contains(relatedTarget) || summary === relatedTarget)) {
              return;
            }
            if (details.contains(relatedTarget) || details === relatedTarget) {
              return;
            }
          }
          
          scheduleClose();
        },
        { signal: this.#abortController.signal }
      );
    };

    // Setup observers for existing details elements
    const detailsElements = this.querySelectorAll(
      'details[is="mega-menu-disclosure"]'
    );
    detailsElements.forEach(setupDetailsObserver);

    // Watch for new details elements being added to DOM
    this.#domObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement) {
            // Check if the added node is a details element
            if (node.matches && node.matches('details[is="mega-menu-disclosure"]')) {
              setupDetailsObserver(node);
            }

            // Check for details elements within the added node
            const nestedDetails = node.querySelectorAll?.(
              'details[is="mega-menu-disclosure"]'
            );
            if (nestedDetails) {
              nestedDetails.forEach(setupDetailsObserver);
            }
          }
        });
      });
    });

    this.#domObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Check if any menu item within the details element is being hovered
   * @param {HTMLElement} details - The details element
   * @returns {boolean}
   */
  #isAnyMenuItemHovered(details) {
    const menuItems = details.querySelectorAll("summary, .mega-menu");
    return Array.from(menuItems).some((item) => item.matches(":hover"));
  }

  /**
   * Setup scroll direction detection for sticky header behavior
   */
  #setupScrollDirection() {
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const scrollTop = document.scrollingElement?.scrollTop ?? 0;
        const isScrollingUp = scrollTop < this.#lastScrollTop;
        
        // Clear any pending timeout
        if (this.#timeout) {
          clearTimeout(this.#timeout);
          this.#timeout = null;
        }

        // Get the section element
        const section = this.closest(`[id^='shopify-section-']`);
        if (!section) {
          ticking = false;
          return;
        }

        if (isScrollingUp) {
          // Scrolling up - show entire header section immediately
          section.removeAttribute("data-animating");
          section.setAttribute("data-scroll-direction", "up");
        } else if (scrollTop > 50) {
          // Scrolling down and past threshold - hide entire header section
          section.setAttribute("data-scroll-direction", "down");
        } else {
          // At top of page - show entire header section
          section.removeAttribute("data-animating");
          section.setAttribute("data-scroll-direction", "none");
        }

        this.#lastScrollTop = scrollTop;
        ticking = false;
      });
    };

    window.addEventListener(
      "scroll",
      handleScroll,
      { passive: true, signal: this.#abortController.signal }
    );

    // Initialize scroll direction
    handleScroll();

    // Set initial state for section
    const section = this.closest(`[id^='shopify-section-']`);
    if (section && !section.hasAttribute("data-scroll-direction")) {
      section.setAttribute("data-scroll-direction", "none");
    }
  }
}

if (!customElements.get("header-od")) {
  customElements.define("header-od", HeaderOD);
}
